/**
 * Document roadbook — annexe A, EF-RBK-005.
 *
 * Le modèle interne est distinct du format d'échange : la conversion se fait
 * ici, en un seul endroit. Aucune valeur enregistrée n'est arrondie,
 * conformément à EF-CAL-008.
 */

import { isCategoryKey, sortCategories, type CategoryKey } from '../core/categories';
import { DomainError, fail, ok, type Result } from '../core/errors';
import {
  formatLocalDateTime,
  isRfc3339,
  nowRfc3339,
  parseLocalDateTime,
  type LocalDateTime,
} from '../core/local-datetime';
import type {
  CoveredRange,
  ManualInputMode,
  OsmType,
  Passage,
  PassageComputed,
  Poi,
  PoiId,
  PresenceState,
  ProgressionInterval,
  Results,
  RoadbookDocument,
  SearchIntegrity,
  SearchParameterSet,
  Stop,
  StopType,
  Totals,
} from '../core/model';
import { EMPTY_RESULTS } from '../core/model';
import type { Parameters } from '../core/parameters';
import { MAX_PASSAGE_REACH_METERS, MIN_PASSAGE_REACH_METERS } from '../geo/passages';
import {
  RESAMPLE_STEP_METERS,
  rehydrateComputedTrack,
  type ComputedTrack,
  type DistanceAnomaly,
  type ElevationSummary,
} from '../geo/track';
import { readDisplay, readGenerator, readParameters, SCHEMA_VERSION, writeDisplay, writeGenerator, writeParameters } from './parameters';
import { invariantError, JsonReader, schemaError } from './reader';

export const ROADBOOK_FORMAT = 'roadbookultra-roadbook';

/** Taille maximale d'un fichier roadbook à l'importation, en octets. */
export const MAX_ROADBOOK_BYTES = 100 * 1024 * 1024;

/** Attribution obligatoire dès qu'un POI d'origine `osm` est présent. */
export const ATTRIBUTION = {
  text: '© OpenStreetMap contributors',
  url: 'https://www.openstreetmap.org/copyright',
  license: 'ODbL 1.0',
} as const;

const OSM_TYPES: readonly OsmType[] = ['node', 'way', 'relation'];
const PRESENCE_STATES: readonly PresenceState[] = [
  'present',
  'absentFromResult',
  'rejectedByFilter',
  'rangeNotCovered',
];
const MANUAL_INPUTS: readonly ManualInputMode[] = ['coordinates', 'progression', 'currentPosition'];
const STOP_TYPES: readonly StopType[] = ['start', 'pause', 'sleep', 'finish'];

/** Coordonnées : degrés décimaux WGS 84, sept décimales au maximum. */
function roundCoordinate(value: number): number {
  return Number(value.toFixed(7));
}

function countCodePoints(text: string): number {
  return [...text.normalize('NFC')].length;
}

// ---------------------------------------------------------------------------
// Écriture
// ---------------------------------------------------------------------------

export interface SerializeInput {
  readonly document: RoadbookDocument;
  readonly parameters: Parameters;
  readonly appVersion: string;
}

export function serializeRoadbook(input: SerializeInput): unknown {
  const { document, parameters } = input;
  const pois = [...document.pois.values()].sort(byFirstPassage);
  const hasOsm = pois.some((poi) => poi.origin === 'osm');
  const track = document.route.track;

  const points: (readonly [number, number, number | null])[] = [];
  for (let i = 0; i < track.count; i += 1) {
    const altitude = track.alt[i];
    points.push([
      roundCoordinate(track.lat[i] ?? 0),
      roundCoordinate(track.lon[i] ?? 0),
      altitude === undefined || Number.isNaN(altitude) ? null : altitude,
    ]);
  }

  const body: Record<string, unknown> = {
    format: ROADBOOK_FORMAT,
    schemaVersion: SCHEMA_VERSION,
    generator: writeGenerator(input.appVersion),
    document: {
      id: document.id,
      title: document.title,
      createdAt: document.createdAt,
      updatedAt: document.updatedAt,
    },
    route: {
      sourceFileName: document.route.sourceFileName,
      selectedElement: {
        type: document.route.selectedElement.type,
        name: document.route.selectedElement.name,
        index: document.route.selectedElement.index,
      },
      originalPointCount: document.route.originalPointCount,
      lengthMeters: track.lengthMeters,
      resampleStepMeters: RESAMPLE_STEP_METERS,
      points,
      elevation: {
        available: track.elevation.available,
        coveragePercent: track.elevation.coveragePercent,
        maxGapMeters: track.elevation.maxGapMeters,
        interpolatedPointCount: track.elevation.interpolatedPointCount,
        filteredPointCount: track.elevation.filteredPointCount,
      },
      elevationGainMeters: track.elevationGainMeters,
      segmentGaps: track.segmentGaps.map(writeAnomaly),
      pointAnomalies: track.pointAnomalies.map(writeAnomaly),
    },
    pois: pois.map(writePoi),
    parameters: writeParameters(parameters, false),
    results: writeResults(document.results),
    display: writeDisplay(parameters.display),
  };

  // L'attribution est obligatoire dès qu'au moins un POI d'origine `osm` est
  // présent, conformément à EF-POI-007.
  if (hasOsm) body['attribution'] = { ...ATTRIBUTION };

  return body;
}

function byFirstPassage(a: Poi, b: Poi): number {
  const left = a.passages[0]?.progressionMeters ?? 0;
  const right = b.passages[0]?.progressionMeters ?? 0;
  if (left !== right) return left - right;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function writeAnomaly(anomaly: DistanceAnomaly): Record<string, unknown> {
  return {
    afterProgressionMeters: anomaly.afterProgressionMeters,
    distanceMeters: anomaly.distanceMeters,
  };
}

function writePoi(poi: Poi): Record<string, unknown> {
  return {
    id: poi.id,
    origin: poi.origin,
    osm:
      poi.osm === null
        ? null
        : { type: poi.osm.type, id: poi.osm.id, matchedTags: { ...poi.osm.matchedTags } },
    categories: [...poi.categories],
    name: poi.name,
    openingHours: poi.openingHours,
    phone: poi.phone,
    email: poi.email,
    website: poi.website,
    accessNote: poi.accessNote,
    coordinate: { lat: roundCoordinate(poi.coordinate.lat), lon: roundCoordinate(poi.coordinate.lon) },
    passageReachMeters: poi.passageReachMeters,
    passages: poi.passages.map(writePassage),
    manualInput: poi.manualInput,
    userState: {
      excluded: poi.userState.excluded,
      pinned: poi.userState.pinned,
      note: poi.userState.note,
      presenceState: poi.userState.presenceState,
      rejectionCause: poi.userState.rejectionCause,
      outOfSearchedPortion: poi.userState.outOfSearchedPortion,
      categoriesChanged: poi.userState.categoriesChanged,
    },
  };
}

function writePassage(passage: Passage): Record<string, unknown> {
  return {
    index: passage.index,
    progressionMeters: passage.progressionMeters,
    distanceToRouteMeters: passage.distanceToRouteMeters,
    computed:
      passage.computed === null
        ? null
        : {
            distanceFromStartMeters: passage.computed.distanceFromStartMeters,
            distanceToFinishMeters: passage.computed.distanceToFinishMeters,
            elevationGainFromStartMeters: passage.computed.elevationGainFromStartMeters,
            elevationGainToFinishMeters: passage.computed.elevationGainToFinishMeters,
            estimatedArrivalLocal: formatLocalDateTime(passage.computed.estimatedArrivalLocal),
            distanceFromPreviousMeters: passage.computed.distanceFromPreviousMeters,
            elevationGainFromPreviousMeters: passage.computed.elevationGainFromPreviousMeters,
            durationFromPreviousSeconds: passage.computed.durationFromPreviousSeconds,
          },
  };
}

function writeResults(results: Results): Record<string, unknown> {
  return {
    computedAt: results.computedAt === null ? null : formatLocalDateTime(results.computedAt),
    mode: results.mode,
    totals:
      results.totals === null
        ? null
        : {
            distanceMeters: results.totals.distanceMeters,
            elevationGainMeters: results.totals.elevationGainMeters,
            ridingSeconds: results.totals.ridingSeconds,
            pauseSeconds: results.totals.pauseSeconds,
            sleepSeconds: results.totals.sleepSeconds,
            totalSeconds: results.totals.totalSeconds,
            startLocal: formatLocalDateTime(results.totals.startLocal),
            finishLocal: formatLocalDateTime(results.totals.finishLocal),
            averageSpeedKmh: results.totals.averageSpeedKmh,
          },
    stops: results.stops.map((stop) => ({
      type: stop.type,
      startLocal: formatLocalDateTime(stop.startLocal),
      endLocal: stop.endLocal === null ? null : formatLocalDateTime(stop.endLocal),
      progressionMeters: stop.progressionMeters,
      durationSeconds: stop.durationSeconds,
      nightIndex: stop.nightIndex,
    })),
    flags: { ...results.flags },
    freshness: results.freshness,
    searchIntegrity: {
      complete: results.searchIntegrity.complete,
      missingChunkCount: results.searchIntegrity.missingChunkCount,
      missingRanges: results.searchIntegrity.missingRanges.map(writeInterval),
      coveredRanges: results.searchIntegrity.coveredRanges.map((range) => ({
        ...writeInterval(range),
        parameterSetId: range.parameterSetId,
        searchedAtLocal: formatLocalDateTime(range.searchedAtLocal),
      })),
      parameterSets: results.searchIntegrity.parameterSets.map((set) => ({
        id: set.id,
        categories: [...set.categories],
        corridorMeters: set.corridorMeters,
        skipStartMeters: set.skipStartMeters,
        skipFinishMeters: set.skipFinishMeters,
      })),
      osmObjectsReceived: results.searchIntegrity.osmObjectsReceived,
      poisRejectedByFilters: results.searchIntegrity.poisRejectedByFilters,
    },
    raceState:
      results.raceState === null
        ? null
        : {
            lastProgressionMeters: results.raceState.lastProgressionMeters,
            lastComputedAtLocal: formatLocalDateTime(results.raceState.lastComputedAtLocal),
          },
  };
}

function writeInterval(interval: ProgressionInterval): Record<string, unknown> {
  return {
    fromProgressionMeters: interval.fromProgressionMeters,
    toProgressionMeters: interval.toProgressionMeters,
  };
}

// ---------------------------------------------------------------------------
// Lecture
// ---------------------------------------------------------------------------

export interface ParsedRoadbook {
  readonly document: RoadbookDocument;
  readonly parameters: Parameters;
}

function readLocalDateTime(reader: JsonReader): LocalDateTime {
  const value = parseLocalDateTime(reader.string());
  if (value === null) {
    throw schemaError(reader.location, 'une date-heure locale `YYYY-MM-DDTHH:mm:ss` est attendue');
  }
  return value;
}

function readRfc3339(reader: JsonReader): string {
  const value = reader.string();
  if (!isRfc3339(value)) throw schemaError(reader.location, 'un horodatage RFC 3339 est attendu');
  return value;
}

function readTrack(reader: JsonReader): ComputedTrack {
  const lengthMeters = reader.required('lengthMeters').number();
  const pointReaders = reader.required('points').array();
  const count = pointReaders.length;

  const lat = new Float64Array(count);
  const lon = new Float64Array(count);
  const alt = new Float64Array(count);

  pointReaders.forEach((pointReader, index) => {
    const triple = pointReader.array();
    const latitude = triple[0];
    const longitude = triple[1];
    const altitude = triple[2];
    if (latitude === undefined || longitude === undefined) {
      throw schemaError(pointReader.location, 'un triplet [latitude, longitude, altitude] est attendu');
    }
    lat[index] = latitude.numberInRange(-90, 90);
    lon[index] = longitude.numberInRange(-180, 180);
    alt[index] = altitude === undefined ? Number.NaN : (altitude.nullable((v) => v.number()) ?? Number.NaN);
  });

  const elevationReader = reader.required('elevation');
  const elevation: ElevationSummary = {
    available: elevationReader.required('available').boolean(),
    coveragePercent: elevationReader.required('coveragePercent').number(),
    maxGapMeters: elevationReader.required('maxGapMeters').number(),
    interpolatedPointCount: elevationReader.optional('interpolatedPointCount').withDefault(0, (v) => v.integer()),
    filteredPointCount: elevationReader.optional('filteredPointCount').withDefault(0, (v) => v.integer()),
  };

  return rehydrateComputedTrack({
    lat,
    lon,
    alt,
    count,
    lengthMeters,
    segmentGaps: reader.optional('segmentGaps').withDefault([], readAnomalies),
    pointAnomalies: reader.optional('pointAnomalies').withDefault([], readAnomalies),
    elevation,
    elevationGainMeters: reader.required('elevationGainMeters').nullable((v) => v.number()),
  });
}

function readAnomalies(reader: JsonReader): readonly DistanceAnomaly[] {
  return reader.array().map((item) => ({
    afterProgressionMeters: item.required('afterProgressionMeters').number(),
    distanceMeters: item.required('distanceMeters').number(),
  }));
}

function readPassageComputed(reader: JsonReader): PassageComputed {
  return {
    distanceFromStartMeters: reader.required('distanceFromStartMeters').number(),
    distanceToFinishMeters: reader.required('distanceToFinishMeters').number(),
    elevationGainFromStartMeters: reader.required('elevationGainFromStartMeters').nullable((v) => v.number()),
    elevationGainToFinishMeters: reader.required('elevationGainToFinishMeters').nullable((v) => v.number()),
    estimatedArrivalLocal: readLocalDateTime(reader.required('estimatedArrivalLocal')),
    distanceFromPreviousMeters: reader.required('distanceFromPreviousMeters').nullable((v) => v.number()),
    elevationGainFromPreviousMeters: reader
      .required('elevationGainFromPreviousMeters')
      .nullable((v) => v.number()),
    durationFromPreviousSeconds: reader.required('durationFromPreviousSeconds').nullable((v) => v.integer()),
  };
}

function readPoi(reader: JsonReader): Poi {
  const id = reader.required('id').string();
  const origin = reader.required('origin').oneOf(['osm', 'manual'] as const);

  const categories: CategoryKey[] = [];
  for (const item of reader.required('categories').array()) {
    const key = item.string();
    if (!isCategoryKey(key)) throw schemaError(item.location, 'clé de catégorie inconnue');
    categories.push(key);
  }

  const osmReader = reader.required('osm');
  const osm = osmReader.nullable((value) => ({
    type: value.required('type').oneOf(OSM_TYPES),
    id: value.required('id').integer(),
    matchedTags: readStringMap(value.optional('matchedTags')),
  }));

  const coordinateReader = reader.required('coordinate');
  const coordinate = {
    lat: coordinateReader.required('lat').numberInRange(-90, 90),
    lon: coordinateReader.required('lon').numberInRange(-180, 180),
  };

  const passages = readPassages(reader);
  const userStateReader = reader.required('userState');

  // Champ hérité : `missingFromLastSearch` produit `absentFromResult` lorsque
  // `presenceState` est absent.
  const legacyMissing = userStateReader.optional('missingFromLastSearch').withDefault(false, (v) => v.boolean());
  const presenceState = userStateReader
    .optional('presenceState')
    .withDefault<PresenceState>(legacyMissing ? 'absentFromResult' : 'present', (v) => v.oneOf(PRESENCE_STATES));

  const note = userStateReader.required('note').nullable((v) => v.string());

  return {
    id,
    origin,
    osm,
    categories: sortCategories(categories),
    name: reader.required('name').nullable((v) => v.string()),
    openingHours: reader.optional('openingHours').withDefault(null, (v) => v.nullable((n) => n.string())),
    phone: reader.optional('phone').withDefault(null, (v) => v.nullable((n) => n.string())),
    email: reader.optional('email').withDefault(null, (v) => v.nullable((n) => n.string())),
    website: reader.optional('website').withDefault(null, (v) => v.nullable((n) => n.string())),
    accessNote: reader.optional('accessNote').withDefault(null, (v) => v.nullable((n) => n.string())),
    coordinate,
    passageReachMeters: reader.required('passageReachMeters').integer(),
    passages,
    manualInput: reader.optional('manualInput').withDefault(null, (v) => v.nullable((n) => n.oneOf(MANUAL_INPUTS))),
    userState: {
      excluded: userStateReader.required('excluded').boolean(),
      pinned: userStateReader.required('pinned').boolean(),
      note,
      presenceState,
      rejectionCause: userStateReader.optional('rejectionCause').withDefault(null, (v) => v.nullable((n) => n.string())),
      outOfSearchedPortion: userStateReader.optional('outOfSearchedPortion').withDefault(false, (v) => v.boolean()),
      categoriesChanged: userStateReader.optional('categoriesChanged').withDefault(false, (v) => v.boolean()),
    },
  };
}

/**
 * Lit les passages, ou reconstruit un passage unique à partir des champs
 * hérités `progressionMeters`, `distanceToRouteMeters` et `computed`.
 */
function readPassages(reader: JsonReader): readonly Passage[] {
  const passagesReader = reader.optional('passages');
  if (!passagesReader.isAbsent && !passagesReader.isNull) {
    const passages = passagesReader.array().map((item, position) => ({
      index: item.optional('index').withDefault(position + 1, (v) => v.integer()),
      progressionMeters: item.required('progressionMeters').number(),
      distanceToRouteMeters: item.required('distanceToRouteMeters').number(),
      computed: item.optional('computed').withDefault(null, (v) => v.nullable(readPassageComputed)),
    }));
    return [...passages].sort((a, b) => a.progressionMeters - b.progressionMeters).map((passage, index) => ({
      ...passage,
      index: index + 1,
    }));
  }

  const legacyProgression = reader.optional('progressionMeters');
  if (legacyProgression.isAbsent) {
    throw schemaError(reader.location, 'au moins un passage est attendu');
  }
  return [
    {
      index: 1,
      progressionMeters: legacyProgression.number(),
      distanceToRouteMeters: reader.optional('distanceToRouteMeters').withDefault(0, (v) => v.number()),
      computed: reader.optional('computed').withDefault(null, (v) => v.nullable(readPassageComputed)),
    },
  ];
}

function readStringMap(reader: JsonReader): Record<string, string> {
  if (reader.isAbsent || reader.isNull) return {};
  const record = reader.object();
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string') result[key] = value;
  }
  return result;
}

function readStop(reader: JsonReader): Stop {
  return {
    type: reader.required('type').oneOf(STOP_TYPES),
    startLocal: readLocalDateTime(reader.required('startLocal')),
    endLocal: reader.optional('endLocal').withDefault(null, (v) => v.nullable(readLocalDateTime)),
    progressionMeters: reader.required('progressionMeters').number(),
    durationSeconds: reader.optional('durationSeconds').withDefault(null, (v) => v.nullable((n) => n.integer())),
    nightIndex: reader.optional('nightIndex').withDefault(null, (v) => v.nullable((n) => n.integer())),
  };
}

function readTotals(reader: JsonReader): Totals {
  return {
    distanceMeters: reader.required('distanceMeters').number(),
    elevationGainMeters: reader.required('elevationGainMeters').nullable((v) => v.number()),
    ridingSeconds: reader.required('ridingSeconds').integer(),
    pauseSeconds: reader.required('pauseSeconds').integer(),
    sleepSeconds: reader.required('sleepSeconds').integer(),
    totalSeconds: reader.required('totalSeconds').integer(),
    startLocal: readLocalDateTime(reader.required('startLocal')),
    finishLocal: readLocalDateTime(reader.required('finishLocal')),
    averageSpeedKmh: reader.required('averageSpeedKmh').number(),
  };
}

function readSearchIntegrity(reader: JsonReader): SearchIntegrity {
  if (reader.isAbsent || reader.isNull) return EMPTY_RESULTS.searchIntegrity;

  const missingRanges: ProgressionInterval[] = reader
    .optional('missingRanges')
    .withDefault<ProgressionInterval[]>([], (value) =>
      value.array().map((item) => ({
        fromProgressionMeters: item.required('fromProgressionMeters').number(),
        toProgressionMeters: item.required('toProgressionMeters').number(),
      })),
    );

  const coveredRanges: CoveredRange[] = reader
    .optional('coveredRanges')
    .withDefault<CoveredRange[]>([], (value) =>
      value.array().map((item) => ({
        fromProgressionMeters: item.required('fromProgressionMeters').number(),
        toProgressionMeters: item.required('toProgressionMeters').number(),
        parameterSetId: item.optional('parameterSetId').withDefault('', (v) => v.string()),
        searchedAtLocal: item
          .optional('searchedAtLocal')
          .withDefault(0 as LocalDateTime, readLocalDateTime),
      })),
    );

  const parameterSets: SearchParameterSet[] = reader
    .optional('parameterSets')
    .withDefault<SearchParameterSet[]>([], (value) =>
      value.array().map((item) => {
        const categories: CategoryKey[] = [];
        for (const key of item.optional('categories').withDefault<string[]>([], (v) => v.array().map((c) => c.string()))) {
          if (isCategoryKey(key)) categories.push(key);
        }
        return {
          id: item.required('id').string(),
          categories,
          corridorMeters: item.optional('corridorMeters').withDefault(0, (v) => v.integer()),
          skipStartMeters: item.optional('skipStartMeters').withDefault(0, (v) => v.integer()),
          skipFinishMeters: item.optional('skipFinishMeters').withDefault(0, (v) => v.integer()),
        };
      }),
    );

  return {
    complete: reader.optional('complete').withDefault(missingRanges.length === 0, (v) => v.boolean()),
    missingChunkCount: reader.optional('missingChunkCount').withDefault(0, (v) => v.integer()),
    missingRanges,
    coveredRanges,
    parameterSets,
    osmObjectsReceived: reader.optional('osmObjectsReceived').withDefault(0, (v) => v.integer()),
    poisRejectedByFilters: reader.optional('poisRejectedByFilters').withDefault(0, (v) => v.integer()),
  };
}

function readResults(reader: JsonReader, track: ComputedTrack): Results {
  const totals = reader.required('totals').nullable(readTotals);
  const stops = reader.optional('stops').withDefault<Stop[]>([], (value) => value.array().map(readStop));

  const flagsReader = reader.optional('flags');
  const flags = {
    sleepSkippedByUser: flagsReader.isAbsent
      ? false
      : flagsReader.optional('sleepSkippedByUser').withDefault(false, (v) => v.boolean()),
    elevationUnavailable: flagsReader.isAbsent
      ? !track.elevation.available
      : flagsReader.optional('elevationUnavailable').withDefault(false, (v) => v.boolean()),
    timezoneWarning: flagsReader.isAbsent
      ? false
      : flagsReader.optional('timezoneWarning').withDefault(false, (v) => v.boolean()),
    needsRecalculation: flagsReader.isAbsent
      ? false
      : flagsReader.optional('needsRecalculation').withDefault(false, (v) => v.boolean()),
  };

  const raceState = reader.optional('raceState').withDefault(null, (value) =>
    value.nullable((state) => ({
      lastProgressionMeters: state.required('lastProgressionMeters').number(),
      lastComputedAtLocal: readLocalDateTime(state.required('lastComputedAtLocal')),
    })),
  );

  return {
    computedAt: reader.optional('computedAt').withDefault(null, (v) => v.nullable(readLocalDateTime)),
    mode: reader.optional('mode').withDefault<'analysis' | 'race'>('analysis', (v) => v.oneOf(['analysis', 'race'] as const)),
    totals,
    // Un document antérieur dont `stops` ne contient que les lignes `pause` et
    // `sleep` est accepté : `start` et `finish` sont reconstruites.
    stops: completeStops(stops, totals, track),
    flags,
    freshness: reader.optional('freshness').withDefault(null, (v) => v.nullable((n) => n.string())),
    searchIntegrity: readSearchIntegrity(reader.optional('searchIntegrity')),
    raceState,
  };
}

function completeStops(stops: readonly Stop[], totals: Totals | null, track: ComputedTrack): readonly Stop[] {
  if (stops.length === 0) return stops;
  if (totals === null) return stops;

  const completed = [...stops];
  if (!completed.some((stop) => stop.type === 'start')) {
    completed.unshift({
      type: 'start',
      startLocal: totals.startLocal,
      endLocal: null,
      progressionMeters: 0,
      durationSeconds: null,
      nightIndex: null,
    });
  }
  if (!completed.some((stop) => stop.type === 'finish')) {
    completed.push({
      type: 'finish',
      startLocal: totals.finishLocal,
      endLocal: null,
      progressionMeters: track.lengthMeters,
      durationSeconds: null,
      nightIndex: null,
    });
  }
  return completed;
}

/**
 * Analyse un document roadbook complet : schéma, puis invariants métier.
 *
 * L'importation réussie ne remplace le document courant qu'après validation
 * complète du fichier : cette fonction ne modifie rien.
 */
export function parseRoadbookFile(payload: unknown): Result<ParsedRoadbook> {
  try {
    const root = JsonReader.root(payload);

    const format = root.required('format').string();
    if (format !== ROADBOOK_FORMAT) {
      return fail(
        new DomainError(
          'document/unknown-format',
          `Le fichier n'est pas un roadbook RoadbookUltra (format « ${format} »).`,
          format,
        ),
      );
    }

    const version = root.required('schemaVersion').integer();
    if (version !== SCHEMA_VERSION) {
      return fail(
        new DomainError(
          'document/unknown-schema-version',
          `Version de schéma inconnue (${version}). Une version plus récente de l'application peut être nécessaire.`,
          String(version),
        ),
      );
    }

    readGenerator(root.required('generator'));

    const documentReader = root.required('document');
    const routeReader = root.required('route');
    const track = readTrack(routeReader);

    const pois = new Map<PoiId, Poi>();
    for (const poiReader of root.required('pois').array()) {
      const poi = readPoi(poiReader);
      pois.set(poi.id, poi);
    }

    const parametersBody = readParameters(root.required('parameters'), false);
    // Le `display` racine fait foi ; `parameters.display` d'une version
    // antérieure est ignoré.
    const display = readDisplay(root.optional('display'));
    const parameters: Parameters = { ...parametersBody, display };

    const results = readResults(root.required('results'), track);

    const document: RoadbookDocument = {
      id: documentReader.required('id').string(),
      title: documentReader.required('title').string(),
      createdAt: readRfc3339(documentReader.required('createdAt')),
      updatedAt: readRfc3339(documentReader.required('updatedAt')),
      route: {
        sourceFileName: routeReader.required('sourceFileName').nullable((v) => v.string()),
        selectedElement: readSelectedElement(routeReader.required('selectedElement')),
        originalPointCount: routeReader.required('originalPointCount').integer(),
        track,
      },
      pois,
      results,
    };

    const violation = checkInvariants(document, root.required('pois').array().length);
    if (violation !== null) return fail(invariantError(violation));

    return ok({ document, parameters });
  } catch (error) {
    if (error instanceof DomainError) return fail(error);
    throw error;
  }
}

function readSelectedElement(reader: JsonReader): RoadbookDocument['route']['selectedElement'] {
  return {
    type: reader.required('type').oneOf(['trk', 'rte'] as const),
    name: reader.required('name').nullable((v) => v.string()),
    index: reader.required('index').integer(),
  };
}

/**
 * Invariants métier de EF-RBK-005. La conformité au schéma ne suffit pas : un
 * document peut être structurellement valide et métier incohérent.
 */
export function checkInvariants(document: RoadbookDocument, declaredPoiCount: number): string | null {
  const track = document.route.track;
  const length = track.lengthMeters;

  if (track.count === 0) return 'la trace de calcul est vide';

  // La longueur déclarée doit être cohérente avec le nombre de points : le pas
  // est de 100 m, le dernier point porte la longueur totale.
  const expectedMinimum = (track.count - 2) * RESAMPLE_STEP_METERS;
  const expectedMaximum = track.count * RESAMPLE_STEP_METERS;
  if (length < expectedMinimum || length > expectedMaximum) {
    return 'la longueur déclarée du parcours est incohérente avec la trace de calcul';
  }

  if (document.pois.size !== declaredPoiCount) {
    return 'les identifiants internes des POI ne sont pas uniques dans le document';
  }

  for (const poi of document.pois.values()) {
    if (poi.categories.length === 0) return `le POI ${poi.id} ne porte aucune catégorie`;
    if (poi.passages.length === 0) return `le POI ${poi.id} ne porte aucun passage`;
    if (
      poi.passageReachMeters < MIN_PASSAGE_REACH_METERS ||
      poi.passageReachMeters > MAX_PASSAGE_REACH_METERS
    ) {
      return `la portée de passage du POI ${poi.id} est hors de l'intervalle 100–5 000 mètres`;
    }
    for (const passage of poi.passages) {
      if (passage.progressionMeters < 0 || passage.progressionMeters > length) {
        return `un passage du POI ${poi.id} est hors du parcours`;
      }
    }
    const note = poi.userState.note;
    if (note !== null && countCodePoints(note) > 500) {
      return `la note du POI ${poi.id} dépasse 500 points de code`;
    }
  }

  const stops = document.results.stops;
  if (stops.length > 0) {
    if (stops[0]?.type !== 'start') return 'results.stops ne commence pas par une ligne Départ';
    if (stops[stops.length - 1]?.type !== 'finish') return 'results.stops ne se termine pas par une ligne Arrivée';
    for (let i = 1; i < stops.length; i += 1) {
      const previous = stops[i - 1];
      const current = stops[i];
      if (previous === undefined || current === undefined) continue;
      if (current.progressionMeters < previous.progressionMeters) {
        return "results.stops n'est pas ordonné par progression croissante";
      }
    }
  }

  const totals = document.results.totals;
  if (totals !== null && totals.finishLocal < totals.startLocal) {
    return "l'heure d'arrivée estimée est antérieure à l'heure de départ";
  }

  const integrity = document.results.searchIntegrity;
  const allRanges = [...integrity.missingRanges, ...integrity.coveredRanges];
  for (const range of allRanges) {
    if (range.fromProgressionMeters < 0 || range.toProgressionMeters > length + 1) {
      return "un intervalle de recherche sort de l'intervalle [0, longueur]";
    }
    if (range.toProgressionMeters < range.fromProgressionMeters) {
      return 'un intervalle de recherche est décroissant';
    }
  }
  if (overlaps(integrity.missingRanges) || overlaps(integrity.coveredRanges)) {
    return 'les intervalles de recherche ne sont pas deux à deux disjoints';
  }
  if (overlapsBetween(integrity.missingRanges, integrity.coveredRanges)) {
    return 'un intervalle couvert recouvre un intervalle manquant';
  }

  return null;
}

function overlaps(ranges: readonly ProgressionInterval[]): boolean {
  const sorted = [...ranges].sort((a, b) => a.fromProgressionMeters - b.fromProgressionMeters);
  for (let i = 1; i < sorted.length; i += 1) {
    const previous = sorted[i - 1];
    const current = sorted[i];
    if (previous === undefined || current === undefined) continue;
    if (current.fromProgressionMeters < previous.toProgressionMeters) return true;
  }
  return false;
}

function overlapsBetween(
  left: readonly ProgressionInterval[],
  right: readonly ProgressionInterval[],
): boolean {
  for (const a of left) {
    for (const b of right) {
      if (a.fromProgressionMeters < b.toProgressionMeters && b.fromProgressionMeters < a.toProgressionMeters) {
        return true;
      }
    }
  }
  return false;
}

/** Nom de fichier proposé à l'exportation — EF-RBK-014. */
export function proposedFileName(title: string): string {
  const normalized = title.normalize('NFC');

  // Un titre ne produisant aucun caractère exploitable — ni lettre, ni chiffre,
  // ni tiret, ni tiret bas — donne le nom de repli.
  if (!/[\p{L}\p{N}\-_]/u.test(normalized)) return 'roadbook.roadbook.json';

  const cleaned = [...normalized]
    .map((character) => (/[\p{L}\p{N} \-_]/u.test(character) ? character : '_'))
    .join('')
    .trim();
  const truncated = [...cleaned].slice(0, 80).join('').trim();
  if (truncated.length === 0) return 'roadbook.roadbook.json';
  return `${truncated}.roadbook.json`;
}

/** Horodatage technique d'une modification métier. */
export function touchDocument(document: RoadbookDocument): RoadbookDocument {
  return { ...document, updatedAt: nowRfc3339() };
}
